/**
 * Client-side schemas for the edge's JSON view payloads (mirrors the interfaces
 * the `apps/api` chat + companion routes emit). Every response the client trusts
 * is re-validated against these at the network boundary — the edge is the
 * authority, but a malformed body is a bug we surface loudly, never render
 * blindly. JSX-free + DOM-free so `bun test` can exercise the parsers directly.
 */
import {
  type DataExportArchive,
  DeletionScheduleView,
  dataExportArchiveSchema,
  GfStatus,
  MediaJobStatus,
  MediaKind,
  NsfwLevel,
  Theme,
  Tier,
  Visibility,
} from '@4f/shared';
import { z } from 'zod';

/**
 * Billing + referral response contracts live in `@4f/shared` — the single
 * Zod-backed source of truth, shared api ↔ backend ↔ frontend (so the two sides
 * cannot drift). Re-exported here so the client's api layer imports EVERY
 * response contract from one module; `client.ts` re-validates each of these at
 * the network boundary exactly like the companion/chat schemas below.
 */
export {
  BillingSummaryView,
  LedgerEntryView,
  PackView,
  PlanView,
  ReferralEarningView,
  ReferralSummaryView,
  ReferredUserView,
  SubscriptionView,
} from '@4f/shared';

/**
 * A companion projected for the GF switcher (`GET /companions`). The pinned
 * `seed` is deliberately absent — the edge withholds it. `updatedAt` is the
 * switcher's sort key (most-recently-active first).
 */
export const CompanionView = z.object({
  id: z.string().min(1),
  accountId: z.string().min(1),
  userId: z.string().min(1),
  name: z.string(),
  buildSource: z.string(),
  nsfwLevel: NsfwLevel,
  status: GfStatus,
  avatarAssetId: z.string().nullable(),
  referenceAssetId: z.string().nullable(),
  persona: z.record(z.string(), z.unknown()),
  appearance: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CompanionView = z.infer<typeof CompanionView>;

/** The switcher list payload. */
export const CompanionList = z.array(CompanionView);
export type CompanionList = z.infer<typeof CompanionList>;

/** A conversation projected for the client (`POST`/`GET /conversations`). */
export const ConversationView = z.object({
  id: z.string().min(1),
  girlfriendId: z.string().min(1),
  title: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ConversationView = z.infer<typeof ConversationView>;

/**
 * One persisted message: role + origin + the AI-SDK content parts. `content`
 * is left as `unknown[]` — the render layer extracts text / directives / media
 * refs from the parts (see `chat/content.ts`), so a new part shape never breaks
 * the parse here.
 */
export const MessageView = z.object({
  id: z.string().min(1),
  role: z.string(),
  origin: z.string(),
  content: z.array(z.unknown()),
  createdAt: z.string(),
});
export type MessageView = z.infer<typeof MessageView>;

/**
 * A conversation with a bounded, chronological page of its MOST RECENT messages
 * (`GET /:id`) — the edge caps the read, so this is the newest page of a long
 * thread, never its head.
 *
 * `hasMore` says older messages exist before `messages[0]` and were not returned.
 * It must be declared here or zod strips it and the truncation the edge went out
 * of its way to report dies at the network boundary. OPTIONAL only for the
 * rollout window (a cached PWA shell can outlive the edge pod that predates the
 * field); absent reads as "not truncated", which is exactly what an edge that
 * does not cap means. Consumers must treat it as `?? false`, never as `boolean`.
 */
export const ConversationDetail = z.object({
  conversation: ConversationView,
  messages: z.array(MessageView),
  hasMore: z.boolean().optional(),
});
export type ConversationDetail = z.infer<typeof ConversationDetail>;

/** The ack returned when answering a parked `waiting_input` form (`POST /:id/input`). */
export const RunHandle = z.object({
  runId: z.string().min(1),
});
export type RunHandle = z.infer<typeof RunHandle>;

/**
 * A finished, stored media asset as the Artifacts gallery renders it — a plain
 * (unbranded) client mirror of the backend `MediaAssetView` (like `CompanionView`
 * mirrors the companion projection): the edge is the authority, the client just
 * re-validates the shape at the boundary. `url` is a ready-to-fetch short-TTL
 * presigned GET, or `null` when the object could not be signed (the tile degrades
 * rather than failing the whole listing).
 */
export const MediaAssetView = z.object({
  assetId: z.string().min(1),
  // Null for a pre-generated prebuilt-catalog asset (no originating user job).
  jobId: z.string().min(1).nullable(),
  girlfriendId: z.string().min(1),
  kind: MediaKind,
  url: z.string().nullable(),
  visibility: Visibility,
  contentType: z.string(),
  width: z.number().int().nonnegative(),
  height: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative().nullable(),
  createdAt: z.string(),
});
export type MediaAssetView = z.infer<typeof MediaAssetView>;

/** Her gallery (`GET /companions/:id/gallery`) — most-recent first. */
export const GalleryList = z.array(MediaAssetView);
export type GalleryList = z.infer<typeof GalleryList>;

/**
 * One generation job's live status (`GET /media/jobs/:id`) — the POLL fallback
 * behind the WS `media_job` push. A backgrounded tab (mobile Safari kills the
 * socket) misses the push entirely, so the client reconciles a still-pending
 * inline placeholder against this instead of spinning forever. A plain client
 * mirror of the shared `MediaJobView`; the edge is the authority.
 */
export const MediaJobStatusView = z.object({
  jobId: z.string().min(1),
  kind: MediaKind,
  status: MediaJobStatus,
  segmentTotal: z.number().int().nonnegative(),
  creditsCostMicros: z.number().nonnegative(),
});
export type MediaJobStatusView = z.infer<typeof MediaJobStatusView>;

/**
 * A rail-hosted checkout hand-off (`POST /billing/checkout/{subscription,topup}`)
 * — the URL the client redirects the browser to, plus the rail key that minted it.
 * Mirrors the edge's `CheckoutView` (`apps/api/src/routes/billing.ts`). NOTHING is
 * granted here: the verified CCBill webhook applies credits, so the client only
 * ever hands off, never assumes a purchase succeeded.
 */
export const CheckoutView = z.object({
  rail: z.string().min(1),
  checkoutUrl: z.string().min(1),
});
export type CheckoutView = z.infer<typeof CheckoutView>;

/**
 * One PREBUILT catalog template as the free-start picker renders it
 * (`GET /companions/prebuilt`) — the seeded `Aria` / `Stella` / `Iris` rows under
 * the catalog account (`scripts/companions/seed-prebuilt.ts`). `previewUrl` is her
 * pre-generated portrait, already signed by the edge (the per-tenant
 * `/media/gallery` read is scoped to the CALLER's account, so it cannot reach a
 * catalog template's assets — the catalog listing has to carry the URL itself),
 * or `null` when unsignable (the card degrades to her initials).
 *
 * CONTRACT ASSUMPTION — the edge route is landing in parallel (`apps/api` is
 * another agent's file set). This is the shape this client validates; a drift
 * surfaces loudly as `malformed_response` rather than rendering `undefined`.
 * Reconciling is a change to this schema + the two paths in `client.ts`.
 */
export const PrebuiltTemplateView = z.object({
  templateId: z.string().min(1),
  /** Stable public key for the template — what a public page names her by. */
  slug: z.string().min(1),
  name: z.string(),
  nsfwLevel: NsfwLevel,
  persona: z.record(z.string(), z.unknown()),
  appearance: z.record(z.string(), z.unknown()),
  previewUrl: z.string().nullable(),
});
export type PrebuiltTemplateView = z.infer<typeof PrebuiltTemplateView>;

/** The prebuilt catalog listing — the free front door's companion choices. */
export const PrebuiltCatalogList = z.array(PrebuiltTemplateView);
export type PrebuiltCatalogList = z.infer<typeof PrebuiltCatalogList>;

/** `POST /push/subscribe` ack — the Web Push target was registered for this account. */
export const PushSubscribedView = z.object({ subscribed: z.literal(true) });
export type PushSubscribedView = z.infer<typeof PushSubscribedView>;

/**
 * The signed-in user the surface renders — the client mirror of the edge's
 * `toPublicUser` projection (`apps/api/src/routes/auth.ts`): identity + the
 * persisted preferences, zero secrets. `ageOk` is the explicit-content gate
 * (`age_verified_at !== null`, derived server-side) the surface reads to decide
 * whether the age step is still owed.
 */
export const AuthUserView = z.object({
  id: z.string().min(1),
  email: z.string(),
  displayName: z.string(),
  ageOk: z.boolean(),
  referralCode: z.string(),
  locale: z.string().nullable(),
  timezone: z.string().nullable(),
  theme: Theme,
  createdAt: z.string(),
});
export type AuthUserView = z.infer<typeof AuthUserView>;

/**
 * A minted session (`POST /auth/{register,login,verify-age,refresh}`) — the
 * short-lived access token the SPA holds in memory + sends as `Authorization:
 * Bearer`, its lifetime, and the public user. The rotating refresh token rides
 * an httpOnly cookie (never in this body, never readable by JS).
 */
export const SessionView = z.object({
  accessToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
  user: AuthUserView,
});
export type SessionView = z.infer<typeof SessionView>;

/** `POST /auth/logout` ack — the refresh family was revoked + the cookie cleared. */
export const LogoutView = z.object({ loggedOut: z.literal(true) });
export type LogoutView = z.infer<typeof LogoutView>;

/** `GET /me` — the caller's identity plus their account tier + cached balance. */
export const MeView = z.object({
  user: AuthUserView,
  account: z.object({ tier: Tier, creditBalanceMicros: z.number() }),
});
export type MeView = z.infer<typeof MeView>;

/**
 * `POST /me/export` — the self-serve data archive (compliance.md § "Data
 * rights"). The SHAPE is the shared contract the edge assembles and parses; the
 * client re-validates it at the network boundary like every other response, so a
 * drifted archive fails here rather than inside a half-written download.
 */
export const DataExportView = dataExportArchiveSchema;
export type DataExportView = DataExportArchive;

/** `POST /me/delete` — the soft-disable + grace schedule the request returns. */
export { DeletionScheduleView };
